import React, { ReactElement, useEffect, useState } from "react";
import { Button, Caption, Card, Detail, Grid, H3, H4, Hr, Icon, Row } from "@maggioli-design-system/react";

import { Link } from "react-router-dom";
import { getAccessToken, replaceMenuName, scrollTop } from "../lib/Functions";
import { expressURL, getCookie } from "../lib/Constants";
import Singleton, { IProdottoAttivo } from "../lib/Singleton";
import ErrorPage from "./ErrorPage";
import { TailSpin } from "react-loader-spinner";
import { jwtDecode } from "jwt-decode";

interface Props {
    idProdotti: any;
    ip: string;
    setShowModal: Function;
}

interface IUserInfo {
    given_name: string;
    family_name: string;
    email: string;
}

export default function AccountPage({ idProdotti, setShowModal }: Props): ReactElement {
    const userIsLogged: boolean = getCookie("access_token") !== null && getCookie("refresh_token") !== null;

    const [prodottiAttivi, setProdottiAttivi] = React.useState<IProdottoAttivo[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [userInfo, setUserInfo] = useState<any>(null);

    useEffect(() => {
        loadProdottiAttivi();
        loadUserInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadUserInfo = async () => {
        if (!userIsLogged) {
            return;
        }
        
        const accessToken = await getAccessToken();
        if(!accessToken) {
            return;
        }
        const decoded = jwtDecode(accessToken);
        setUserInfo(decoded);
    }

    const loadProdottiAttivi = async () => {
        if (!userIsLogged) {
            return;
        }
        const accessToken = Singleton.getActiveToken();
        setLoading(true);
        const res = await fetch(`${expressURL}/api/users/profile`, {
            method: "POST",
            headers: {
                "Authorization": "Bearer " + accessToken,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ id_prodotti: idProdotti.map((p) => p.id).join(",") }),
        });
        setLoading(false);
        if (!res.ok) {
            return;
        }
        const data = await res.json();
        if (!Array.isArray(data)) {
            return;
        }
        const subProducts = [];
        data.forEach((p) => {
            subProducts.push(
                ...p.sub_products.map((sp) => {
                    return {
                        ...sp,
                        scadenza: p.scadenza,
                    };
                }),
            );
        });
        setProdottiAttivi([...data, ...subProducts]);
    };
    if (!userIsLogged) {
        return <ErrorPage errorNum={401} title="Eseguire l'accesso prima di visualizzare la pagina" />;
    }

    // Converte una stringa in data
    const fromStringToDate = (date: string): string => {
        if (!date) {
            return "";
        }
        const temp: string[] = date.split(" ");
        const temp2: string[] = temp[0].split("-");
        return `${temp2.pop()}-${temp2.pop()}-${temp2.pop()}`;
    };

    const idProdottiAttivi: string[] = prodottiAttivi.map((p) => p.id_prodotto);

    const singleExpiringDate = (idProdotto: string) => {
        const prodotto = prodottiAttivi.filter((p) => p.id_prodotto === idProdotto.toString())[0];
        if (prodotto) {
            return fromStringToDate(prodotto.scadenza);
        }
        const prodotto200 = prodottiAttivi.filter((p) => p.id_prodotto === "200")[0];
        if (prodotto200) {
            return fromStringToDate(prodotto200.scadenza);
        }
        return false;
    };

    return (
        <div className="padding-normal background-color-adjust-tone-19">
            {/* Titolo */}
            <H3 className="text-center vertical-padding-xxsmall">I miei abbonamenti</H3>

            <Grid className="view-limit lg:px-8" columns="1" gutter="small" rows="fit-vertically">
                {/* Nome utente */}
                <Detail>{/* Nome utente: <b>{userdata.name}</b>{" "} */}</Detail>

                <Row lastChild="to-right">
                    <H4>L'abbonamento è attivo per le aree evidenziate</H4>
                    <Button variant="success-outline" horizontalPadding="none" onClick={() => setShowModal(true)}>
                        Estendi abbonamento
                    </Button>
                </Row>
                {loading ? (
                    <div className="mx-auto">
                        <TailSpin color="#0041B9" />
                    </div>
                ) : (
                    <Grid columns="3">
                        {idProdotti
                            .filter((p) => p.id > 201)
                            .map((p, i) => {
                                const isActive: boolean = idProdottiAttivi.includes(p.id) || idProdottiAttivi.includes("200");
                                const href: string = `/categoria/${p.descrizione.replaceAll(" ", "-").toLowerCase()}/${p.id}/1`;
                                return (
                                    <Link key={i} to={href}>
                                        <Card padding="small" className={isActive ? "bg-status-success-19" : ""}>
                                            <Row lastChild="to-right">
                                                <Grid gutter="xxsmall">
                                                    <Detail>{replaceMenuName(p.descrizione)}</Detail>
                                                    <Caption>{singleExpiringDate(p.id) ? singleExpiringDate(p.id) : "---"}</Caption>
                                                </Grid>
                                                {isActive ? <Icon name="crud-save" /> : <div></div>}
                                            </Row>
                                        </Card>
                                    </Link>
                                );
                            })}
                    </Grid>
                )}

            </Grid>

            
            { !loading &&
            <>
                <H3 className="text-center vertical-padding-xxsmall mt-10">Il mio profilo</H3>
                <Grid className="view-limit lg:px-8" columns="1" gutter="xsmall" rows="fit-vertically">
                    <Row lastChild="to-right">
                        <Grid gutter="none">
                            <H4>Dati di accesso unico al network Maggioli</H4>
                            <Detail>Queste credenziali potranno essere utilizzate come dati di accesso unico a tutti i siti del network Maggioli Editore.</Detail>
                        </Grid>
                        <Button icon="navigate-external-link" variant="primary-outline" horizontalPadding="none" onClick={() => window.open("https://sso.maggioli.com/realms/maggioli/account/?referrer=bibliotecadigitale", '_blank')}>
                            Modifica dati
                        </Button>
                    </Row>
                    <hr className="color-adjust-tone-13"/>
                    { userInfo !== null &&
                    <Grid columns="3" className="mt-5">
                        <Grid gutter="none">
                            <H4>Nome</H4>
                            <Detail>{userInfo.given_name}</Detail>
                        </Grid>
                        <Grid gutter="none">
                            <H4>Cognome</H4>
                            <Detail>{userInfo.family_name}</Detail>
                        </Grid>
                        <Grid gutter="none">
                            <H4>Email</H4>
                            <Detail>{userInfo.email}</Detail>
                        </Grid>
                        <Grid gutter="none">
                            <H4>Password</H4>
                            <Detail>******</Detail>
                        </Grid>
                    </Grid> }
                </Grid>
            </>}
            
            <div className="view-limit lg:px-8 mt-5">
                <hr className="color-adjust-tone-13"/>
                <Grid columns="3" className="mt-5">
                    <div className="bg-adjust-tone-17 rounded-lg p-3">
                        <Grid gutter="xsmall">
                            <Detail><strong>Servizio clienti</strong></Detail>
                            <Caption>
                                Disponibile dal Lunedì al Venerdì
                                <br />
                                Dalle 08:30 alle 17:30
                            </Caption>
                            <Caption>Tel. +39 0541 628200</Caption>
                            <Detail className="color-brand-maggioli-03">
                                <Link to={"/contatti"} onClick={() => scrollTop()}>
                                    Contattaci
                                </Link>
                            </Detail>
                        </Grid>
                    </div>
                </Grid>
            </div>
            
        </div>
    );
}
